import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import {
  researchCms,
  fetchAllEditionBpas,
  type FirmRankingDetail,
  type LawyerRankingDetail,
  type RankingStatus,
} from "@/api/researchCms.service";
import { CmsEmptyState } from "@/components/ResearchCms";
import { UnsavedChangesModal } from "@/components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { SUBTYPE_LABELS } from "./components/RankingsTab/RankingsTab";
import { countryFlag } from "./utils/countryFlag";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsRankingTablePage.module.css";

const STATUS_LABELS: Record<RankingStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  published: "Published",
  archived: "Archived",
};

type RankingEntry = {
  id: string;
  entityId: string;
  name: string;
  firmName: string | null;
  officeName: string | null;
  rankingTier: number | null;
  displayOrder: number;
  status: RankingStatus;
  comment: string | null;
};

function sortEntries(entries: RankingEntry[]): RankingEntry[] {
  const allSameOrder = entries.every(e => e.displayOrder === entries[0]?.displayOrder);
  if (allSameOrder) {
    return [...entries].sort((a, b) => a.name.localeCompare(b.name));
  }
  return [...entries].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
  );
}

function firmToEntries(rankings: FirmRankingDetail[]): RankingEntry[] {
  return sortEntries(
    rankings.map(r => ({
      id: r.id,
      entityId: r.companyId,
      name: r.firmName ?? "Unknown firm",
      firmName: null,
      officeName: r.officeName !== r.firmName ? r.officeName : null,
      rankingTier: r.rankingTier,
      displayOrder: r.displayOrder,
      status: r.status,
      comment: r.rankingComment,
    })),
  );
}

function lawyerToEntries(rankings: LawyerRankingDetail[]): RankingEntry[] {
  return sortEntries(
    rankings.map(r => ({
      id: r.id,
      entityId: r.contactId,
      name: r.displayName ?? r.contactName ?? "Unknown lawyer",
      firmName: r.firmName,
      officeName: r.officeName !== r.firmName ? r.officeName : null,
      rankingTier: r.rankingTier,
      displayOrder: r.displayOrder,
      status: r.status,
      comment: r.rankingComment,
    })),
  );
}

function groupByTier(entries: RankingEntry[]): Array<{ tier: number; entries: RankingEntry[] }> {
  const map = new Map<number, RankingEntry[]>();
  for (const entry of entries) {
    if (entry.rankingTier === null) continue;
    const group = map.get(entry.rankingTier);
    if (group) group.push(entry);
    else map.set(entry.rankingTier, [entry]);
  }
  return [...map.entries()].sort(([a], [b]) => a - b).map(([tier, entries]) => ({ tier, entries }));
}

function GripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
      <circle cx="2" cy="2" r="1.5" />
      <circle cx="8" cy="2" r="1.5" />
      <circle cx="2" cy="8" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="2" cy="14" r="1.5" />
      <circle cx="8" cy="14" r="1.5" />
    </svg>
  );
}

type SortableRowProps = {
  entry: RankingEntry;
  index: number;
  isLawyer: boolean;
  canDrag: boolean;
  onClick: () => void;
};

function SortableRow({ entry, index, isLawyer, canDrag, onClick }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: !canDrag,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.row} ${isDragging ? styles.rowDragging : ""}`}
    >
      {canDrag && (
        <button
          type="button"
          className={styles.dragHandle}
          {...listeners}
          {...attributes}
          aria-label={`Reorder ${entry.name}`}
        >
          <GripIcon />
        </button>
      )}
      <span className={styles.rowIndex}>{entry.displayOrder}</span>
      <button type="button" className={styles.rowBody} onClick={onClick}>
        <span className={styles.rowName}>
          <span className={styles.rowNameLine}>
            {entry.name}
            {isLawyer && entry.firmName && (
              <span className={styles.rowFirm}> · {entry.firmName}</span>
            )}
          </span>
          {entry.officeName && <span className={styles.rowOffice}>{entry.officeName}</span>}
        </span>
        <span className={styles.rowMeta}>
          <span className={`${styles.statusPill} ${styles[`status_${entry.status}`] ?? ""}`}>
            {STATUS_LABELS[entry.status]}
          </span>
          {entry.comment && (
            <span className={styles.rowComment} title={entry.comment}>
              {entry.comment.length > 50 ? `${entry.comment.slice(0, 50)}...` : entry.comment}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

type StaticRowProps = {
  entry: RankingEntry;
  index: number;
  isLawyer: boolean;
  onClick: () => void;
};

function StaticRow({ entry, index, isLawyer, onClick }: StaticRowProps) {
  return (
    <div className={styles.row}>
      <span className={styles.rowIndex}>{entry.displayOrder}</span>
      <button type="button" className={styles.rowBody} onClick={onClick}>
        <span className={styles.rowName}>
          <span className={styles.rowNameLine}>
            {entry.name}
            {isLawyer && entry.firmName && (
              <span className={styles.rowFirm}> · {entry.firmName}</span>
            )}
          </span>
          {entry.officeName && <span className={styles.rowOffice}>{entry.officeName}</span>}
        </span>
        <span className={styles.rowMeta}>
          <span className={`${styles.statusPill} ${styles[`status_${entry.status}`] ?? ""}`}>
            {STATUS_LABELS[entry.status]}
          </span>
          {entry.comment && (
            <span className={styles.rowComment} title={entry.comment}>
              {entry.comment.length > 50 ? `${entry.comment.slice(0, 50)}...` : entry.comment}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

export function CmsRankingTablePage() {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    editionId: routeEditionId,
    bpaId: routeBpaId,
    rankingTableId: routeRankingTableId,
  } = useParams<{ editionId: string; bpaId: string; rankingTableId: string }>();

  const editionId = routeEditionId!;
  const bpaId = routeBpaId!;
  const rankingTableId = routeRankingTableId!;

  const canEdit = hasPermission("staff.research.edit");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [localEntries, setLocalEntries] = useState<RankingEntry[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const navGuard = useNavigationGuard(dirty);

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["research-ranking-table-page", editionId, bpaId, rankingTableId],
    queryFn: async () => {
      const [allBpas, editionsResult, tableResult] = await Promise.all([
        fetchAllEditionBpas({ editionId }),
        researchCms.getEditions({ editionId, limit: 1 }),
        researchCms.getRankingTable(rankingTableId),
      ]);
      const bpa = allBpas.find(b => b.bpaId === bpaId);
      const edition = editionsResult.editionConfigs[0];
      return {
        bpaName: bpa?.bpaName ?? "",
        rankingEntityName: bpa?.rankingEntityName ?? "",
        rankingEntityId: bpa?.rankingEntityId ?? "",
        editionName: edition?.editionName ?? "",
        table: tableResult.rankingTable,
      };
    },
  });

  const table = apiData?.table;
  const isLawyerTable = table?.isLawyerTable === 1;
  const isTiered = table?.isTiered === 1;
  const canReorder = canEdit;

  // Derived from apiData/table; computed here (rather than after the loading guards below) so
  // handleRowClick's useCallback below can close over them without a temporal-dead-zone error.
  const editionName = apiData?.editionName ?? "";
  const bpaName = apiData?.bpaName ?? "";
  const rankingEntityName = apiData?.rankingEntityName ?? "";
  const rankingEntityId = apiData?.rankingEntityId ?? "";
  const tableName = table?.name ?? "";

  const { data: rankingsData, isLoading: rankingsLoading } = useQuery({
    queryKey: ["research-ranking-table-entries", rankingTableId, isLawyerTable],
    queryFn: async () => {
      if (isLawyerTable) {
        const data = await researchCms.getLawyerRankings(rankingTableId);
        return { entries: lawyerToEntries(data.lawyerRankings) };
      }
      const data = await researchCms.getFirmRankings(rankingTableId);
      return { entries: firmToEntries(data.firmRankings) };
    },
    enabled: table !== undefined,
    staleTime: 0,
  });

  const entries = localEntries ?? rankingsData?.entries ?? [];
  const tierGroups = useMemo(() => (isTiered ? groupByTier(entries) : null), [isTiered, entries]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!canReorder) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const current = localEntries ?? rankingsData?.entries ?? [];
      const oldIndex = current.findIndex(e => e.id === active.id);
      const newIndex = current.findIndex(e => e.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(current, oldIndex, newIndex);
      setLocalEntries(reordered);
      setDirty(true);
    },
    [canReorder, localEntries, rankingsData],
  );

  const handleSave = useCallback(async () => {
    if (!localEntries || !dirty) return;
    setSaving(true);
    try {
      const items = localEntries.map((e, i) => ({ id: e.id, displayOrder: i }));
      if (isLawyerTable) {
        await researchCms.reorderLawyerRankings(rankingTableId, items);
      } else {
        await researchCms.reorderFirmRankings(rankingTableId, items);
      }
      setDirty(false);
      setLocalEntries(null);
      queryClient.invalidateQueries({
        queryKey: ["research-ranking-table-entries", rankingTableId],
      });
      setToast({ message: "Order saved", type: "success" });
    } catch (error) {
      console.error("Failed to reorder rankings:", error);
      setToast({ message: "Failed to save order", type: "error" });
    } finally {
      setSaving(false);
    }
  }, [localEntries, dirty, isLawyerTable, rankingTableId, queryClient]);

  const isSubmittable = table?.submissionType != null && table.submissionType !== "none";

  const handleRowClick = useCallback(
    (entry: RankingEntry) => {
      if (saving) return;
      const base = isLawyerTable
        ? ROUTES.research.cmsIndividual(editionId, bpaId, entry.entityId)
        : ROUTES.research.cmsOrg(editionId, bpaId, entry.entityId);
      const path = isSubmittable ? `${base}?rankingTable=${rankingTableId}` : base;
      navigate(path, {
        state: {
          bpaName,
          rankingEntityId: rankingEntityId || undefined,
          rankingEntityName,
          editionName,
          companyName: !isLawyerTable ? entry.name : undefined,
          entityName: entry.name,
          rankingTableName: tableName,
          hasMultipleTables: true,
        },
      });
    },
    [
      saving,
      isLawyerTable,
      isSubmittable,
      editionId,
      bpaId,
      rankingTableId,
      navigate,
      bpaName,
      rankingEntityId,
      rankingEntityName,
      editionName,
      tableName,
    ],
  );

  if (!hasPermission("staff.research.read")) {
    return <UnauthorizedView />;
  }

  if (apiLoading) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: "Loading...",
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Loading..." />
        <CmsEmptyState message="Loading ranking table" loading />
      </CmsLayout>
    );
  }

  const subtypeLabel = SUBTYPE_LABELS[table?.subtype ?? ""] ?? table?.subtype ?? "";

  return (
    <CmsLayout
      breadcrumbs={[
        { label: editionName, path: ROUTES.research.cmsPublication(editionId) },
        ...(rankingEntityName
          ? [
              {
                label: rankingEntityName,
                ...(rankingEntityId
                  ? {
                      path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${rankingEntityId}`,
                    }
                  : {}),
              },
            ]
          : []),
        { label: bpaName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
        { label: `${subtypeLabel}: ${tableName}` },
      ]}
    >
      <div className={styles.headerBar}>
        <div className={styles.headerContent}>
          <span className={styles.pageLabel}>Ranking Table</span>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{tableName}</h1>
            <span className={styles.subtypeBadge}>{subtypeLabel}</span>
          </div>
          <div className={styles.meta}>
            {rankingEntityName && (
              <span className={styles.metaText}>
                {countryFlag(rankingEntityName) && (
                  <span className={styles.flag}>{countryFlag(rankingEntityName)}</span>
                )}
                {rankingEntityName}
              </span>
            )}
            {tableName !== bpaName && <span className={styles.metaText}>{bpaName}</span>}
            <span className={styles.metaText}>
              {isTiered
                ? `${tierGroups?.reduce((n, g) => n + g.entries.length, 0) ?? 0} ranked`
                : `${entries.length} ${isLawyerTable ? "lawyers" : "firms"}`}
            </span>
          </div>
        </div>
      </div>

      <div className={`${styles.content} ${saving ? styles.contentSaving : ""}`}>
        {!rankingsLoading && entries.length > 0 && (
          <p className={styles.dragHint}>
            {canReorder
              ? isTiered
                ? "Drag rankings to reorder within the same tier. Click a row to open the ranking."
                : "Drag rankings to reorder. Click a row to open the ranking."
              : "Click a row to open the ranking."}
          </p>
        )}
        {rankingsLoading ? (
          <CmsEmptyState message="Loading rankings" loading />
        ) : entries.length === 0 ? (
          <CmsEmptyState message="No rankings in this table yet." />
        ) : isTiered && tierGroups ? (
          <div className={styles.groups}>
            {tierGroups.map(({ tier, entries: groupEntries }) => (
              <div key={tier} className={styles.tierGroup}>
                <div className={styles.tierHeading}>
                  <span className={styles.tierBadge}>{tier}</span>
                  <span className={styles.tierLabel}>Tier {tier}</span>
                  <span className={styles.tierCount}>{groupEntries.length} ranked</span>
                </div>
                {canReorder ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={groupEntries.map(e => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className={styles.list}>
                        {groupEntries.map((entry, i) => (
                          <SortableRow
                            key={entry.id}
                            entry={entry}
                            index={i}
                            isLawyer={isLawyerTable}
                            canDrag={!saving}
                            onClick={() => handleRowClick(entry)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className={styles.list}>
                    {groupEntries.map((entry, i) => (
                      <StaticRow
                        key={entry.id}
                        entry={entry}
                        index={i}
                        isLawyer={isLawyerTable}
                        onClick={() => handleRowClick(entry)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : canReorder ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={entries.map(e => e.id)} strategy={verticalListSortingStrategy}>
              <div className={styles.list}>
                {entries.map((entry, i) => (
                  <SortableRow
                    key={entry.id}
                    entry={entry}
                    index={i}
                    isLawyer={isLawyerTable}
                    canDrag={!saving}
                    onClick={() => handleRowClick(entry)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className={styles.list}>
            {entries.map((entry, i) => (
              <StaticRow
                key={entry.id}
                entry={entry}
                index={i}
                isLawyer={isLawyerTable}
                onClick={() => handleRowClick(entry)}
              />
            ))}
          </div>
        )}
      </div>

      {dirty && (
        <div className={shared.footer}>
          <button
            type="button"
            className={shared.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save order"}
          </button>
        </div>
      )}

      {navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            setLocalEntries(null);
            setDirty(false);
            navGuard.proceed();
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
